import { ClientSideError, ClientSideErrorType, logger } from '@dtx-company/logger'
import { PaymentIntent, SetupIntent, Stripe } from '@stripe/stripe-js'
import { SubscriptionClientSecret } from '@dtx-company/inter-app/src/api/subscription-service/types/subscriptionV3/SubscriptionClientSecret'
import { additionalPaymentSetupIntentVerification } from '@app/common/src/components/PlanPurchaseExperience/utils/additionalPaymentSetupIntentVerification'
import { fireAnalyticsEvent } from '@dtx-company/inter-app/src/event-tracking/helpers/fireAnalyticsEvent'
import { handlePaymentIntentSetupIntentErrors } from '@app/common/src/components/PlanPurchaseExperience/utils/handlePaymentIntentSetupIntentErrors'

export const confirmPaymentIntentSetupIntent = async (
  clientSecret: SubscriptionClientSecret,
  stripe: Stripe,
  redirectURL: string,
  paymentMethod: string
): Promise<void> => {
  try {
    fireAnalyticsEvent('ConfirmPaymentIntentSetupIntent_Started', {
      paymentType: clientSecret.type,
      paymentMethod: paymentMethod
    })
    let result: PaymentIntent | SetupIntent | undefined
    let error
    if (clientSecret.type === 'paymentIntent') {
      const res = await stripe.confirmPayment({
        clientSecret: clientSecret.clientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: redirectURL
        }
      })
      result = res.paymentIntent
      error = res.error
    } else {
      const res = await stripe.confirmSetup({
        clientSecret: clientSecret.clientSecret,
        redirect: 'if_required',
        confirmParams: {
          return_url: redirectURL
        }
      })
      result = res.setupIntent
      error = res.error
    }
    if (paymentMethod === 'cashapp') {
      const intentStatus = await getPaymentSetupIntentStatus(clientSecret, stripe)
      if (intentStatus !== 'succeeded') {
        await handlePaymentIntentSetupIntentErrors({
          paymentError: error,
          paymentIntent:
            clientSecret.type === 'paymentIntent' ? (result as PaymentIntent) : undefined,
          setupIntent: clientSecret.type !== 'paymentIntent' ? (result as SetupIntent) : undefined
        })
      }
    } else if (result) {
      if (result.status == 'requires_action') {
        await additionalPaymentSetupIntentVerification(
          stripe,
          result.client_secret || '',
          result.next_action?.type
        )
      } else {
        await handlePaymentIntentSetupIntentErrors({
          paymentError: error,
          paymentIntent:
            clientSecret.type === 'paymentIntent' ? (result as PaymentIntent) : undefined,
          setupIntent: clientSecret.type !== 'paymentIntent' ? (result as SetupIntent) : undefined
        })
      }
    } else {
      await handlePaymentIntentSetupIntentErrors({
        paymentError: error,
        paymentIntent: clientSecret.type === 'paymentIntent' ? result : undefined,
        setupIntent: clientSecret.type !== 'paymentIntent' ? result : undefined
      })
    }
    fireAnalyticsEvent('ConfirmPaymentIntentSetupIntent_Submitted', {
      paymentType: clientSecret.type,
      paymentMethod: paymentMethod,
      additionalPaymentVerification: result?.next_action?.type,
      success: true
    })
  } catch (e) {
    if (paymentMethod === 'cashapp') {
      const intentStatus = await getPaymentSetupIntentStatus(clientSecret, stripe)
      if (intentStatus !== 'succeeded') {
        fireAnalyticsEvent('ConfirmPaymentIntentSetupIntent_Submitted', {
          paymentType: clientSecret.type,
          paymentMethod: paymentMethod,
          success: false
        })
        const error = new ClientSideError({
          name: e.name,
          message: e.message,
          code: e.code,
          type: ClientSideErrorType.ClientSideError
        })
        logger.logError(error, { technicalArea: 'monetization' })

        throw error
      } else {
        return Promise.resolve()
      }
    } else {
      fireAnalyticsEvent('ConfirmPaymentIntentSetupIntent_Submitted', {
        paymentType: clientSecret.type,
        paymentMethod: paymentMethod,
        success: false
      })
      const error = new ClientSideError({
        name: e.name,
        message: e.message,
        code: e.code,
        type: ClientSideErrorType.ClientSideError
      })
      if (!e.type) {
        logger.logError(error, { technicalArea: 'monetization' })
      }

      throw error
    }
  }
}

const getPaymentSetupIntentStatus = async (
  clientSecret: SubscriptionClientSecret,
  stripe: Stripe
): Promise<string> => {
  let intentStatus = 'succeeded'
  if (clientSecret.type === 'paymentIntent') {
    const paymentIntent = await stripe.retrievePaymentIntent(clientSecret.clientSecret)
    intentStatus = paymentIntent.paymentIntent?.status || 'succeeded'
  } else {
    const setupIntent = await stripe.retrieveSetupIntent(clientSecret.clientSecret)
    intentStatus = setupIntent.setupIntent?.status || 'succeeded'
  }
  return intentStatus
}
